import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../../i18n";
import { cn } from "../../utils";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../Pagination/PageSizeControls";
import { TableContextProvider } from "./context";
import { usePaginationController } from "./hooks/usePaginationController";
import { useRowController } from "./hooks/useRowDensity";
import { useSelectionController } from "./hooks/useSelectionController";
import { useSortController } from "./hooks/useSortController";
import { TableBody } from "./TableBody";
import { TableEmptyState } from "./TableEmptyState";
import { TableFooter } from "./TableFooter";
import { TableHeader } from "./TableHeader";
import type { TableContextType, TableProps } from "./types";
import { getGridTemplateColumns } from "./utils";

export function Table<Item>(properties: TableProps<Item>) {
  const { t } = useTranslation();
  const {
    items,
    pagination,
    sorting,
    columns,
    selectable,
    loading,
    emptyState,
    skeletonRows = DEFAULT_PAGE_SIZE_OPTIONS[0],
  } = properties;

  const showFooter = !loading && !emptyState;

  const { sortedItems, sortConfig, onSort } = useSortController({
    items,
    sorting,
    columns,
  });
  const { paginatedItems, paginationConfig, resetPage } =
    usePaginationController({
      items: sortedItems,
      pagination,
    });

  const rowDensity = useRowController({
    rowDensity: properties.rowDensity,
  });

  const selection = useSelectionController({
    items: sortedItems,
    itemsOnCurrentPage: paginatedItems,
    selectable,
    pagination: paginationConfig,
  });

  const [previousSortSignature, setPreviousSortSignature] = useState<
    string | null
  >(null);

  useEffect(() => {
    const sortSignature = sortConfig
      ? `${sortConfig.key}-${sortConfig.direction}`
      : null;

    if (pagination === true && sortSignature !== previousSortSignature) {
      if (previousSortSignature !== null) {
        resetPage();
      }
      setPreviousSortSignature(sortSignature);
    }
  }, [
    sortConfig?.key,
    sortConfig?.direction,
    pagination,
    previousSortSignature,
    resetPage,
  ]);

  const gridTemplateColumns = useMemo(() => {
    return getGridTemplateColumns(columns, !!selection);
  }, [columns, selection]);

  const context: TableContextType<Item> = {
    ...properties,
    items: paginatedItems,
    pagination: paginationConfig,
    sorting: sortConfig,
    onSort,
    rowDensity,
    selection,
    gridTemplateColumns,
  };

  return (
    <TableContextProvider value={context}>
      <div
        className={cn(
          "flex flex-col flex-shrink min-h-0 rounded-lg border border-neutral-500/5 overflow-hidden select-none bg-white",
          properties.className,
        )}
      >
        <div
          className="flex flex-col flex-grow overflow-auto"
          role="grid"
          aria-label={t("table.dataTableAriaLabel")}
          tabIndex={-1}
        >
          <TableHeader />
          {emptyState ? (
            <TableEmptyState />
          ) : (
            <TableBody<Item> skeletonRows={skeletonRows} />
          )}
        </div>
        {showFooter && <TableFooter />}
      </div>
    </TableContextProvider>
  );
}
